import { Accordion, AccordionDetails, AccordionSummary, Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import { ImageLoader } from '../imgLoader/imgLoader';
import ClosetIcon from '../../logo/closet.png';
import { SalesPoint } from 'orderme-api-integration-client';
import { useEffect, useRef, useState } from 'react';
import { clearDelivery, deliverToBusinessLocker } from '../../redux/reducers/pickupReducer';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { LockerSelector, LockerSelectorRef } from './lockerSelector';
import { DeliveryDetails } from './whereTo';
import { ItemDescription } from './itemDescription';
import { RootState } from '../../redux/store';

interface LockerItemProps {
  deliveryDetails: DeliveryDetails;
  onClick: () => void;
  isSelected: boolean;
}

export function LockerItem(props: LockerItemProps) {
  const { deliveryDetails, onClick, isSelected } = props;
  const { t } = useTranslation(['payment']);
  const dispatch = useAppDispatch();
  const [locker, setLocker] = useState<SalesPoint | null>(null);
  const LockerSelectorRef = useRef<LockerSelectorRef>(null);
  const totalPrice = useAppSelector((state: RootState) => state.shoppingCartState.totalPrice);
  const priceUpToFreeDelivery =
    deliveryDetails.freeDeliveryFrom > 0 ? deliveryDetails.freeDeliveryFrom - totalPrice : 0;
  const isEnabled = priceUpToFreeDelivery <= 0 || deliveryDetails.price > 0;
  const description =
    priceUpToFreeDelivery > 0
      ? [
          t('payment:businessLockerFreeDeliveryFrom', {
            price: deliveryDetails.freeDeliveryFrom.toPrice(),
          }),
          t('payment:priceUpToFreeDelivery', {
            price: priceUpToFreeDelivery.toPrice(),
          }),
        ]
      : [];

  useEffect(() => {
    if (!isSelected) {
      setLocker(null);
      if (LockerSelectorRef.current) {
        LockerSelectorRef.current.clear();
      }
      return;
    }
  }, [isSelected]);

  useEffect(() => {
    if (locker) {
      dispatch(deliverToBusinessLocker({ lockerId: locker.id }));
    } else {
      dispatch(clearDelivery());
    }
  }, [locker, dispatch]);

  const onAccordionClick = () => {
    if (!isSelected && isEnabled) {
      onClick();
    }
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', my: '20px' }}>
      <Accordion expanded={isSelected} sx={{ backgroundColor: !isEnabled && '#D2CDC3', width: '100%' }}>
        <AccordionSummary
          aria-controls="panel-content"
          id="panel-header"
          onClick={onAccordionClick}
          style={{ cursor: 'pointer' }}
        >
          <Box>
            <Box display="flex" alignItems="center" sx={{ width: '100%' }}>
              {isSelected ? (
                <RadioButtonCheckedIcon color="secondary" />
              ) : (
                <RadioButtonUncheckedIcon sx={{ color: 'rgba(0, 0, 0, 0.54);' }} />
              )}
              <Box display="flex" alignItems="center" sx={{ width: '65px', ml: '10px' }}>
                <ImageLoader src={ClosetIcon} sx={{ height: '50px' }} alt="SalesPoint" />
              </Box>
              <Typography
                sx={{
                  alignContent: 'center',
                  ml: '10px',
                  color: !isEnabled && 'rgba(0, 0, 0, 0.54);',
                }}
              >
                {t('salesPoint:businessLocker')}
              </Typography>
            </Box>

            <ItemDescription description={description} />
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ px: '20px', ml: '15px', color: 'black' }}>
            <LockerSelector ref={LockerSelectorRef} onFound={(o) => setLocker(o)} onNotFound={() => setLocker(null)} />
          </Box>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
}
